import {internalApi} from "../../common/axios";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import toast from "react-hot-toast";
import {STALE_TIME} from "../../shared/utils/basic";
import {ProbabilityEntity} from "./ProbabilityEntity";

const fetchProbabilityData = async () => {
    const {data} = await internalApi.get('upload/probability');
    return data.rows;
}

export const useProbabilityData = () => {
    return useQuery({
        queryKey: ['probabilityData'],
        queryFn: fetchProbabilityData,
        staleTime: STALE_TIME
    });
};

const updateProbabilityData = async (newData) => {
    const { data } = await internalApi.post('upload/probability', { file: newData });
    return data;
}

export const useUpdateProbabilityData = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateProbabilityData,
        onSuccess: () => {
            toast.success('데이터가 성공적으로 업데이트되었습니다.');
            queryClient.invalidateQueries({ queryKey: ['probabilityData'] });
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || '업데이트에 실패했습니다.');
        }
    });
};

const fetchNewProbabilityData = async () => {
    const {data} = await internalApi.get('upload/new_probability');
    return data.rows.map(item => new ProbabilityEntity(item));
}

export const useNewProbabilityData = () => {
    return useQuery({
        queryKey: ['newProbabilityData'],
        queryFn: fetchNewProbabilityData,
        staleTime: STALE_TIME
    });
};

const saveNewProbabilityData = async ({seq, body, onOff}) => {
    const { data } = await internalApi.post('upload/new_probability', { seq, body, onOff });
    return data;
}

export const useSaveNewProbabilityData = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: saveNewProbabilityData,
        onSuccess: () => {
            toast.success('데이터가 성공적으로 저장되었습니다.');
            queryClient.invalidateQueries({ queryKey: ['newProbabilityData'] });
            queryClient.invalidateQueries({ queryKey: ['newProbabilityDataBySeq'] });
            queryClient.invalidateQueries({ queryKey: ['probabilityData'] });
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || '저장에 실패했습니다.');
        }
    });
}

const deleteNewProbabilityData = async (seq) => {
    const { data } = await internalApi.delete('upload/new_probability', { data: { seq } });
    return data;
}

export const useDeleteNewProbabilityData = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteNewProbabilityData,
        onSuccess: () => {
            toast.success('데이터가 성공적으로 삭제되었습니다.');
            queryClient.invalidateQueries({ queryKey: ['newProbabilityData'] });
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || '삭제에 실패했습니다.');
        }
    });
}

const getNewProbabilityDataBySeq = async (seq) => {
    const {data} = await internalApi.get('upload/new_probability/by_seq', { params: { seq } });
    if(!data?.rows || data.rows.length === 0) throw new Error('데이터를 찾을 수 없습니다.');
    return new ProbabilityEntity(data.rows[0]);
}

export const useGetNewProbabilityDataBySeq = (seq) => {
    return useQuery({
        queryKey: ['newProbabilityDataBySeq', seq],
        queryFn: () => getNewProbabilityDataBySeq(seq),
        staleTime: STALE_TIME,
        enabled: !!seq
    });
};

const saveNewProbabilityOnOff = async ({seq, onOff}) => {
    const { data } = await internalApi.post('upload/new_probability/onoff', { seq, onOff });
    return data;
}

export const useSaveNewProbabilityOnOff = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: saveNewProbabilityOnOff,
        onSuccess: () => {
            toast.success('데이터가 성공적으로 저장되었습니다.');
            queryClient.invalidateQueries({ queryKey: ['newProbabilityData'] });
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || '저장에 실패했습니다.');
        }
    });
}

const newUploadProbabilityData = async () => {
    await internalApi.put('upload/new_probability');
}

export const useNewUploadProbabilityData = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: newUploadProbabilityData,
        onSuccess: () => {
            toast.success('데이터가 성공적으로 업로드되었습니다.');
            queryClient.invalidateQueries({ queryKey: ['probabilityData'] });
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || '업로드에 실패했습니다.');
        }
    });
};

const testUploadProbabilityData = async () => {
    await internalApi.put('upload/new_probability/test_upload');
}

export const useTestUploadProbabilityData = () => {
    return useMutation({
        mutationFn: testUploadProbabilityData,
        onSuccess: () => {
            toast.success('테스트 데이터가 성공적으로 업로드되었습니다.');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || '테스트 업로드에 실패했습니다.');
        }
    });
};